import useCart from 'components/Cart/Cart.hooks';
import PromoCode from 'components/PromoCode';
import React, { MouseEventHandler, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError } from 'utils/helpers';

import usePromoCodeActions from './hooks/usePromoCodeActions';

export type CartPromoCodeProps = {
  // If true, the component will display a loading spinner when applying or removing a promo code
  showLoadingState?: boolean;
  // Callback to notify the parent component when the loading state changes
  onLoadingChange?: (isLoading: boolean) => void;
  // If false, the component will hide the advertised promos even if available and the feature flag is enabled
  hideAdvertisedPromos?: boolean;
  /**
   * Shows lightGrey border instead of uiGrey
   */
  isVariant?: boolean;
};

/**
 * Display the promocode applied to the order. Otherwise display the input to add a new one.
 */
function CartPromoCode(props: CartPromoCodeProps): JSX.Element {
  const {
    showLoadingState,
    onLoadingChange,
    hideAdvertisedPromos = false,
    isVariant = false,
  } = props;
  const { order } = useCart();

  const { handleSubmit, formState, control } = useForm<{
    promoCode: string;
  }>({
    defaultValues: {
      promoCode: '',
    },
  });

  const { applyPromoMutation, removeAllPromosMutation } = usePromoCodeActions();

  const promotion = order?.promotions?.[0] ?? null;

  const promoCodeSubmitHandler = (formValues: { promoCode: string }) => {
    if (!order) {
      return;
    }

    applyPromoMutation.mutate(
      {
        orderId: order.id,
        code: formValues.promoCode,
      },
      {
        onError: (error: any) => {
          logError('Error applying promo code', error);

          const errorMessage =
            error?.response?.data?.errors?.[0] ||
            error?.response?.data?.errors?.[0]?.message ||
            error.message;

          analytics.track({
            event: ANALYTICS.EVENTS.PROMO_ERROR,
            data: {
              error: errorMessage,
              promoCode: formValues.promoCode,
            },
          });
        },
      },
    );
  };

  const removePromoCodeHandler: MouseEventHandler<HTMLButtonElement> = () => {
    if (!order) {
      return;
    }

    removeAllPromosMutation.mutate({ orderId: order.id });
  };

  const isLoading =
    applyPromoMutation.isLoading || removeAllPromosMutation.isLoading;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  return (
    <PromoCode
      promotion={promotion?.code}
      showLoadingState={showLoadingState}
      isLoading={isLoading}
      applyPromoMutation={applyPromoMutation}
      removePromoCodeHandler={removePromoCodeHandler}
      handleSubmit={handleSubmit}
      formState={formState}
      control={control}
      promoCodeSubmitHandler={promoCodeSubmitHandler}
      showAdvertisedPromos={!hideAdvertisedPromos}
      isVariant={isVariant}
    />
  );
}

export default CartPromoCode;
